import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Images } from 'lucide-react';
import type { Benefit } from '@yp/types';
import { useAuth } from '../context/AuthContext';
import { getAdminBenefits, createBenefit, updateBenefit } from '../lib/api';
import ImagePickerModal from '../components/ImagePickerModal';

const DEFAULT_FORM: Partial<Benefit> = {
  title_id: '',
  title_jp: '',
  description_id: '',
  description_jp: '',
  icon_url: '',
  sort_order: 0,
  is_active: true,
};

export default function BenefitFormPage() {
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [form, setForm] = useState<Partial<Benefit>>(DEFAULT_FORM);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEdit);
  const [error, setError] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!isEdit || !accessToken || !id) return;
    getAdminBenefits(accessToken)
      .then((data) => {
        const found = data.find((b) => b.id === id);
        if (found) setForm(found);
      })
      .finally(() => setIsFetching(false));
  }, [isEdit, accessToken, id]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!accessToken) return;
    setError('');
    setIsLoading(true);
    try {
      if (isEdit && id) {
        await updateBenefit(accessToken, id, form);
      } else {
        await createBenefit(accessToken, form);
      }
      navigate('/benefits');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsLoading(false);
    }
  }

  const field = (key: keyof Benefit) => ({
    value: (form[key] ?? '') as string,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value })),
  });

  if (isFetching) {
    return (
      <div className="flex justify-center py-20">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-pink-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate('/benefits')}
          className="rounded-lg p-1.5 text-gray-400 hover:bg-white/5 hover:text-gray-200"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-semibold text-gray-100">
          {isEdit ? 'Edit Benefit' : 'Add Benefit'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Title ID */}
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-400">
            Judul (Bahasa Indonesia) <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            required
            {...field('title_id')}
            placeholder="e.g. Kartu Anggota & Badge"
            className="w-full rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 placeholder:text-gray-600 focus:border-pink-500/50 focus:outline-none"
          />
        </div>

        {/* Title JP */}
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-400">
            タイトル（日本語） <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            required
            {...field('title_jp')}
            placeholder="e.g. メンバーカード＆バッジ"
            className="w-full rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 placeholder:text-gray-600 focus:border-pink-500/50 focus:outline-none"
          />
        </div>

        {/* Description ID */}
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-400">
            Deskripsi (Bahasa Indonesia)
          </label>
          <textarea
            rows={3}
            {...field('description_id')}
            placeholder="Deskripsi singkat manfaat ini..."
            className="w-full resize-none rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 placeholder:text-gray-600 focus:border-pink-500/50 focus:outline-none"
          />
        </div>

        {/* Description JP */}
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-400">
            説明（日本語）
          </label>
          <textarea
            rows={3}
            {...field('description_jp')}
            placeholder="この特典の簡単な説明..."
            className="w-full resize-none rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 placeholder:text-gray-600 focus:border-pink-500/50 focus:outline-none"
          />
        </div>

        {/* Icon */}
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-400">Icon</label>
          <div className="flex gap-3">
            {form.icon_url && (
              <img src={form.icon_url} alt="icon preview" className="h-12 w-12 rounded-lg object-contain bg-white/5 p-1" />
            )}
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-[var(--color-border)] px-4 py-3 text-xs text-gray-500 hover:border-pink-500/30 hover:text-pink-400 transition-colors"
            >
              <Images size={16} />
              Pilih Gambar
            </button>
          </div>
        </div>

        {/* Sort order + is_active */}
        <div className="flex flex-wrap gap-5">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-400">Sort Order</label>
            <input
              type="number"
              min={0}
              value={form.sort_order ?? 0}
              onChange={(e) => setForm((prev) => ({ ...prev, sort_order: Number(e.target.value) }))}
              className="w-24 rounded-lg border border-[var(--color-border)] bg-black/20 px-3 py-2 text-sm text-gray-200 focus:border-pink-500/50 focus:outline-none"
            />
          </div>
          <div className="flex items-end pb-1">
            <label className="flex cursor-pointer items-center gap-2.5">
              <input
                type="checkbox"
                checked={form.is_active ?? true}
                onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                className="h-4 w-4 rounded accent-pink-500"
              />
              <span className="text-sm text-gray-300">Aktif (tampil di homepage)</span>
            </label>
          </div>
        </div>

        {error && (
          <p className="rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</p>
        )}

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate('/benefits')}
            className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-gray-400 hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="rounded-lg px-6 py-2 text-sm font-medium text-white disabled:opacity-60"
            style={{ backgroundColor: 'var(--yp-pink)' }}
          >
            {isLoading ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Benefit'}
          </button>
        </div>
        <ImagePickerModal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onSelect={(url) => setForm((prev) => ({ ...prev, icon_url: url }))}
          defaultFolder="benefits"
        />
      </form>
    </div>
  );
}
